import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Send, Wrench } from "lucide-react";
import ApprovalPrompt from "./ApprovalPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sre-agent`;

const WELCOME_MESSAGE = {
  role: "assistant",
  content:
    "👋 **Welcome to the SRE Agent!**\n\n" +
    "I can help you operate Signal_X services safely.\n\n" +
    "🔍 **Diagnose** — scan the stack, check service health, tail logs\n\n" +
    "🔧 **Restart** — backend, telegram publisher, nginx (with one-click approval)\n\n" +
    "🚀 **Deploy** — pull latest from `main` and redeploy everything (requires typed confirmation)\n\n" +
    "Click a suggestion below or type your own question.",
};

const ChatPanel = ({ sessionId }) => {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState({});
  const endRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/default-questions`)
      .then(({ data }) => setCategories(data.categories || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.response,
          tools: data.tools_used,
          pending: data.pending_approval,
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `⚠️ ${e?.response?.data?.detail || e?.message || "Request failed"}`,
          isError: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onApprovalResolved = (result) => {
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content: `🔧 ${result.message || ""}`,
        tools: [],
      },
    ]);
  };

  const hasUserMessages = messages.some((m) => m.role === "user");
  const showSuggestions = !hasUserMessages && Object.keys(categories).length > 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Suggestions — capped height, scrolls independently if too tall */}
      {showSuggestions && (
        <div
          className="flex-shrink-0 max-h-[35vh] overflow-y-auto p-4 grid gap-3 border-b border-slate-700"
          data-testid="sre-default-questions"
        >
          {Object.entries(categories).map(([key, cat]) => (
            <div
              key={key}
              className="bg-slate-700/50 rounded-lg p-3 border border-slate-600"
            >
              <div className="text-sm font-semibold text-slate-100 mb-2">
                {cat.icon} {cat.title}
              </div>
              <div className="flex flex-wrap gap-2">
                {cat.questions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => send(q)}
                    className="text-sm px-3 py-2 rounded-md bg-slate-600 hover:bg-slate-500 border border-slate-500 text-white transition-colors"
                    data-testid={`sre-suggested-${key}-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Messages — flex-1 min-h-0 so it claims remaining space and scrolls internally */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3" data-testid="sre-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-lg px-4 py-3 ${
                m.role === "user"
                  ? "bg-amber-600/40 border border-amber-400/40 text-white"
                  : m.isError
                  ? "bg-red-500/20 border border-red-500/40 text-red-100"
                  : "bg-slate-700/70 border border-slate-600 text-slate-50"
              }`}
            >
              {m.role === "assistant" && (
                <div className="flex items-center gap-1.5 text-xs text-amber-300 mb-2 font-medium">
                  <Wrench className="w-3.5 h-3.5" /> SRE Agent
                  {m.tools && m.tools.length > 0 && (
                    <span className="ml-1 opacity-70 font-normal">· {m.tools.join(", ")}</span>
                  )}
                </div>
              )}
              <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">
                {m.content}
              </pre>
              {m.pending && (
                <ApprovalPrompt
                  sessionId={sessionId}
                  pending={m.pending}
                  onResolved={onApprovalResolved}
                />
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Loader2 className="w-4 h-4 animate-spin" /> thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input — ALWAYS docked at bottom */}
      <div className="flex-shrink-0 p-4 border-t border-slate-600 bg-slate-800">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            disabled={busy}
            placeholder="Ask about Signal_X services, restart, deploy…"
            data-testid="sre-input"
            className="flex-1 rounded-lg bg-slate-700/70 border border-slate-600 px-4 py-2.5 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition-colors"
          />
          <button
            onClick={() => send()}
            disabled={busy || !input.trim()}
            data-testid="sre-send"
            className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-white text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
